/**
 * Agent runs registry — backend-owned source of truth.
 *
 * Replaces the old `useResearchRunsStore` (zustand persist + localStorage)
 * which couldn't tell when a backgrounded run had completed because nothing
 * patched the persisted status. The truth now lives on the session document
 * in Cosmos and is written by `ExplorerDeepApproach` as the run progresses.
 *
 * The hook polls every 30s by default (no harm if active runs are completing
 * within the window) and accepts WS-driven invalidation through the exported
 * query key. Foreground updates therefore feel instant; reload-recovery
 * works because Cosmos always wins on read.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import {
  type AgentRunModel,
  cancelAgentRunApi,
  dismissAgentRunApi,
  listAgentRunsApi,
  markAgentRunReadApi,
} from "@/api/api"
import { SettingsService } from "@/services/settings-service"

export const AGENT_RUNS_QUERY_KEY = ["agent-runs"] as const

const LEGACY_STORE_KEY = "deep-research-runs"

/**
 * Drop the abandoned localStorage entry from the previous client-side
 * registry. Safe to call repeatedly — `removeItem` is a no-op when the
 * key isn't there. Module-load side effect because it should run exactly
 * once per page load before any hook renders the (empty) zustand snapshot.
 */
if (typeof window !== "undefined") {
  try {
    window.localStorage.removeItem(LEGACY_STORE_KEY)
  } catch {
    /* private-mode browsers throw on storage access; ignore */
  }
}

type AgentRunStatus = AgentRunModel["status"]

const ACTIVE_STATUSES: ReadonlySet<AgentRunStatus> = new Set(["executing", "synthesizing"])

export function isActiveAgentRunStatus(status: AgentRunStatus | undefined): boolean {
  return status !== undefined && ACTIVE_STATUSES.has(status)
}

export function useAgentRunsQuery() {
  return useQuery({
    queryKey: AGENT_RUNS_QUERY_KEY,
    queryFn: () => listAgentRunsApi(SettingsService.getInstance().token),
    // Poll faster while runs are active; back off to 2 min when idle. WS
    // terminal events still invalidate for instant updates while the page is open.
    // When there's no data yet (initial fetch failed or cache cleared), keep
    // polling at the idle cadence so the badge/panel can recover on its own —
    // returning false here would disable polling permanently until a manual refetch.
    refetchInterval: (query) => {
      const runs = query.state.data
      if (!runs) return 120_000
      const hasActive = runs.some((r) => isActiveAgentRunStatus(r.status))
      return hasActive ? 30_000 : 120_000
    },
    staleTime: 30_000,
  })
}

/** Convenience: only the runs the panel surfaces (active + unread terminal). */
export function useVisibleAgentRuns() {
  const { data } = useAgentRunsQuery()
  return data ?? []
}

export function useAgentRunCounts() {
  const runs = useVisibleAgentRuns()
  let active = 0
  let unread = 0
  for (const r of runs) {
    if (isActiveAgentRunStatus(r.status)) active += 1
    else if (r.unread) unread += 1
  }
  return { active, unread, total: runs.length }
}

export function useMarkAgentRunRead() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      markAgentRunReadApi(sessionId, SettingsService.getInstance().token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
    },
  })
}

export function useDismissAgentRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      dismissAgentRunApi(sessionId, SettingsService.getInstance().token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
    },
  })
}

export function useCancelAgentRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (sessionId: string) =>
      cancelAgentRunApi(sessionId, SettingsService.getInstance().token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
    },
  })
}

/** Imperative invalidation for callers outside the React tree (e.g. WS handlers). */
export function invalidateAgentRuns(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: AGENT_RUNS_QUERY_KEY })
}
